import { yupResolver } from "@hookform/resolvers/yup";
import { type FC, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import type { AuthFeatureData } from "~/core";
import { Eye } from "~/icons";
import { Button } from "~/shared/ui-kit";
import { Card } from "~/shared/ui-kit/card/Card";
import { Checkbox } from "~/shared/ui-kit/checkbox/Checkbox";
import { Flex } from "~/shared/ui-kit/flex/Flex";
import { TextField } from "~/shared/ui-kit/text-field/TextField";
import { Title } from "~/shared/ui-kit/title/Title";
import { Typography } from "~/shared/ui-kit/typography/Typography";
import type { LoginCredential } from "~/shared/utils/auth-poker-sdk/auth-poker-sdk";

import { createAuthValidationSchema } from "./auth.schema";

export type ServerErrors = string;

export type AuthData = ExtractArrayItemType<
  NonNullable<AuthFeatureData>["auth"]
>;
export type AuthorizationFormProps = {
  data: AuthData;
  serverError?: ServerErrors;
  onSubmit: (data: LoginCredential) => void;
  onForgotPassword: () => void;
};

export const AuthorizationForm: FC<AuthorizationFormProps> = ({
  data,
  onSubmit,
  serverError,
  onForgotPassword,
}) => {
  const { handleSubmit, control } = useForm<LoginCredential>({
    defaultValues: { isRememberPassword: false },
    resolver: yupResolver(
      createAuthValidationSchema({
        validation_required: data?.validation_required ?? "Field is required!",
      }),
    ),
  });
  const [isVisible, setIsVisible] = useState(false);

  const [serverErrorState, setServerErrorState] = useState<
    ServerErrors | undefined
  >(serverError);

  useEffect(() => {
    setServerErrorState(serverError);
  }, [serverError]);

  return (
    <Card
      variant="secondary"
      className="md:max-w-[524px] min-h-[572px] md:min-h-[640px] rounded-2xl md:rounded-lg"
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="px-4 md:px-10 py-6 md:py-8  flex-grow flex flex-col"
      >
        <Title
          as="h2"
          size="small"
          weight="bold"
          className="text-center md:text-start pb-4 md:pb-6"
        >
          {data?.form_title}
        </Title>
        <Flex gap={8} flexDirection="column">
          <Controller
            control={control}
            name="nick"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                name="nick"
                label={data?.form_input_label_name ?? "Nickname/email"}
                errorMessage={error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="password"
            render={({ field, fieldState: { error } }) => (
              <TextField
                type={isVisible ? "text" : "password"}
                {...field}
                onChange={(e) => {
                  setServerErrorState(undefined);
                  field.onChange(e);
                }}
                name="password"
                label={data?.form_input_label_password ?? "Password"}
                errorMessage={error?.message ?? serverErrorState}
                icon={
                  <Eye
                    onClick={() => setIsVisible(!isVisible)}
                    className="cursor-pointer"
                    viewBox="0 0 24 24"
                  />
                }
              />
            )}
          />
        </Flex>
        <Flex justify="space-between" className="mt-1 md:mt-6">
          <Controller
            control={control}
            name="isRememberPassword"
            rules={{ required: false }}
            render={({ field, fieldState: { error } }) => (
              <Checkbox
                aria-label="label"
                errorMessage={error?.message}
                {...field}
              >
                <Typography
                  weight="normal"
                  size="small"
                  md={{ size: "medium" }}
                  as="span"
                >
                  {data?.form_remember_checkbox_text ?? "Remember password"}
                </Typography>
              </Checkbox>
            )}
          />

          <Typography
            onClick={onForgotPassword}
            as="span"
            weight="normal"
            size="small"
            md={{ size: "medium" }}
            className="underline cursor-pointer"
          >
            {data?.form_restore_link_text ?? "Forgot password?"}
          </Typography>
        </Flex>

        <Button
          type="submit"
          size="small"
          md={{ size: "medium" }}
          weight="semibold"
          className="w-full md:py-4 py-3 mt-auto"
        >
          {data?.form_submit_text}
        </Button>
      </form>
    </Card>
  );
};
